import {
  Box,
  Button,
  Heading,
  InlineStack,
  PlainModal,
  PlainModalDialog,
  PlainModalOverlay,
  Stack,
  Text,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import { PlumaModal } from '@customerio/pluma-components/css/components';
import clsx from 'clsx';
import { FlexMark } from '@/components/flex-mark';
import type { CreditDisplayPurchaseProps } from '@/features/billing';
import { CreditPurchaseButton } from '@/features/billing-usage';
import { SLOW_01_DURATION } from '@/utils/motion';
import { FlexPlanSheetBody } from './components/flex-plan-sheet-body';
import * as styles from './flex-plan-sheet.css';

// Pluma's ModalPrimitive prop is in milliseconds; our token is in seconds.
const SHEET_ANIMATION_DURATION_MS = Math.round(SLOW_01_DURATION * 1000);

type FlexPlanSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  onCloseComplete?: () => void;
  addFunds: CreditDisplayPurchaseProps;
};

/** Full-height sheet that explains the Flex plan and what it costs. */
export function FlexPlanSheet({ isOpen, onClose, onCloseComplete, addFunds }: FlexPlanSheetProps) {
  // The Add funds modal replaces the sheet rather than stacking on top of it.
  const handleAddFunds = () => {
    onClose();
    addFunds.onPurchase?.();
  };

  return (
    <PlainModal
      isOpen={isOpen}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      animationTransitionDuration={SHEET_ANIMATION_DURATION_MS}
    >
      <PlainModalOverlay className={clsx(PlumaModal.classNames.modalOverlay, styles.overlay)}>
        <PlainModalDialog className={styles.sheet} aria-labelledby="flex-plan-sheet-title">
          <Box className={styles.header}>
            <Stack gap="050" pt="400" pb="200" className={styles.column}>
              <InlineStack gap="150" alignBlock="center">
                <FlexMark size="lg" />
                <Heading level="2" id="flex-plan-sheet-title">
                  Flex plan
                </Heading>
              </InlineStack>
              <Text color="secondary">Free to start, then pay as you go. One plan, two ways to pay.</Text>
            </Stack>
            <Button
              variant="tertiary"
              icon="close"
              isIconOnly={true}
              aria-label="Close"
              onClick={onClose}
              className={styles.closeButton}
            />
          </Box>
          <Box className={styles.body}>
            <Box pt="300" pb="400" className={styles.column}>
              <FlexPlanSheetBody />
            </Box>
          </Box>
          <Box className={styles.footer}>
            <InlineStack gap="150" alignBlock="center" alignInline="space-between" py="150" className={styles.column}>
              <Text size="xs" color="secondary">
                Unused funds don't expire. Auto top-up is optional.
              </Text>
              <CreditPurchaseButton
                label="Add funds"
                testId="flex-plan-sheet-add-funds"
                variant="primary"
                elementBefore={<FlexMark tone="solid" />}
                onPurchase={handleAddFunds}
                isPurchaseDisabled={addFunds.isPurchaseDisabled}
                purchaseDisabledMessage={addFunds.purchaseDisabledMessage}
              />
            </InlineStack>
          </Box>
        </PlainModalDialog>
      </PlainModalOverlay>
    </PlainModal>
  );
}

export type FlexPlanSheetModalData = {
  addFunds: CreditDisplayPurchaseProps;
};

/** The sheet as a managed modal, for a flow that opens it without a component to hold its state. */
export function FlexPlanSheetModal({ data, onClose, modalState }: PlumaManagedModalProps<FlexPlanSheetModalData>) {
  return (
    <FlexPlanSheet
      isOpen={modalState.isOpen === true}
      onClose={() => onClose()}
      onCloseComplete={modalState.onCloseComplete}
      addFunds={data.addFunds}
    />
  );
}
